/**
 * Deployment-protection wall detection.
 *
 * The Fastify API ALWAYS answers errors with a hand-serialized JSON envelope
 * (`{ success:false, error:{ code, message } }`, `content-type: application/json`
 * — see `apps/api/components/plugins/errorHandler.ts`). So a 401/403 whose body
 * is HTML did NOT come from our app: the request was intercepted upstream by a
 * platform auth gate — most commonly Vercel Deployment Protection on a
 * preview/staging alias when the `x-vercel-protection-bypass` secret is absent
 * or its ~1h SSO cookie has lapsed. That page carries `content-type: text/html`
 * and a Vercel SSO redirect.
 *
 * This is NOT an authentication failure we can fix by refreshing the token or
 * session cookie — the request never reached the auth plugin. Detecting it lets
 * an API client short-circuit to the stable `DEPLOYMENT_PROTECTED` code instead
 * of (a) burning a useless token/session refresh and (b) surfacing a code-less
 * 401 the UI can't distinguish from a dead session — the exact thing that sent
 * operators into the "sign out and back in" dead-end.
 *
 * Pure and dependency-free on purpose: the Firebase-free public payment client
 * imports it too, and it stays trivially unit-testable.
 */

/** Stable code carried on the rejected error and resolved by the shared ERROR_CATALOG. */
export const DEPLOYMENT_PROTECTED_CODE = 'DEPLOYMENT_PROTECTED' as const;

interface ProtectionProbe {
  status?: number;
  contentType?: unknown;
  body?: unknown;
}

/** Core predicate over the already-extracted response bits. */
export function looksLikeDeploymentProtection({ status, contentType, body }: ProtectionProbe): boolean {
  if (status !== 401 && status !== 403) return false;
  const ct = String(contentType ?? '').toLowerCase();
  const bodyText = typeof body === 'string' ? body : '';
  // Our API only ever emits JSON on errors. An HTML body on a 401/403 is, by
  // definition, an upstream wall and not our envelope.
  return ct.includes('text/html') || /^\s*<(?:!doctype|html)/i.test(bodyText);
}

/**
 * Detect the wall from an axios-style error (`error.response.{status,headers,data}`).
 */
export function isDeploymentProtectionResponse(error: unknown): boolean {
  const response = (
    error as {
      response?: { status?: number; headers?: Record<string, unknown>; data?: unknown };
    }
  )?.response;
  if (!response) return false;
  return looksLikeDeploymentProtection({
    status: response.status,
    contentType: response.headers?.['content-type'],
    body: response.data,
  });
}
